import * as React from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  Backdrop,
  Dialog,
  DialogContent,
  DialogActions,
  OutlinedInput,
  InputAdornment,
  IconButton,
} from "@mui/material";
import type { BackdropProps } from "@mui/material/Backdrop";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { AlertComponent } from "../../../common/components";

import MemorialGoLogo from "../../../assets/media/memgo-logo.svg";
import LockIcon from "../../../assets/icons/lock-icon.svg";
import { providerResetPassword } from "../../../api/auth";

import type ResetPasswordPopupProps from "./reset-password-popup.props";
import "./reset-password-popup.scss";

/** Same backdrop as Login / Forgot */
const ProviderBackdrop = React.forwardRef<HTMLDivElement, BackdropProps>(
  function ProviderBackdrop(props, ref) {
    return (
      <Backdrop
        ref={ref}
        {...props}
        className={`provider-login-backdrop ${props.className ?? ""}`}
      >
        <div className="provider-login-backdrop__footer">
          © 2026 MemorialGo. All rights reserved.
        </div>
      </Backdrop>
    );
  }
);

const validationSchema = Yup.object({
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .max(30, "Password must not exceed 30 characters")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/[0-9]/, "Password must contain at least one number")
    .matches(
      /[!@#$%^&*(),.?":{}|<>_\-\\[\]\/~`+=;]/,
      "Password must contain at least one special character"
    )
    .required("New password is required"),

  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], "Passwords do not match")
    .required("Please confirm your password"),
});


const ResetPasswordPopup: React.FC<ResetPasswordPopupProps> = ({
  open,
  onClose,
  token,
  titleText = "Reset Password",
  subtitleText = "Enter New Password",
  onSuccess,
}) => {
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [successMsg, setSuccessMsg] = React.useState("");
  const [alertMessage, setAlertMessage] = React.useState("");
  const [alertType, setAlertType] = React.useState<
    "success" | "error" | "info" | "warning"
    >("info");
  const [alertOpen, setAlertOpen] = React.useState(false);

  const handleMouseDownPassword = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
  };

  const handleSubmit = async (
    values: { password: string; confirmPassword: string },
    { setSubmitting }: any
  ) => {
    setSuccessMsg("");
    setAlertOpen(false);
    setAlertMessage("");

    if (!token?.trim()) {
    const msg = "Reset token missing or invalid. Please use the link again.";

    setAlertType("error");
    setAlertMessage(msg);
    setAlertOpen(true);

    setSubmitting(false);
    return;
    }

    try {
      const res = await providerResetPassword({
        token,
        newPassword: values.password.trim(),
        confirmPassword: values.confirmPassword.trim(),
      });

      if (res?.data?.success) {
        const msg =
            res?.data?.data?.message ||
            "Your password has been reset successfully. Please log in again.";

        //  inline success (keep)
        setSuccessMsg(msg);

        //  alert success
        setAlertType("success");
        setAlertMessage(msg);
        setAlertOpen(true);

        setTimeout(() => {
            if (onSuccess) onSuccess();
        }, 800);
        } else {
        const msg = "Unable to reset password. Please try again.";

        //  alert error
        setAlertType("error");
        setAlertMessage(msg);
        setAlertOpen(true);
        }

    } catch (err: any) {
      const msg =
            err?.response?.data?.message ||
            err?.response?.data?.error ||
            err?.message ||
            "Something went wrong. Please try again.";

        setAlertType("error");
        setAlertMessage(msg);
        setAlertOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        if (reason === "backdropClick") return;
        onClose();
      }}
      className="reset-password-popup-wrapper"
      slots={{ backdrop: ProviderBackdrop }}
    >
      <DialogContent>
        <div className="header-wrapper">
          <img
            src={MemorialGoLogo}
            alt="MemorialGo Logo"
            className="memorial-go-logo-img"
          />
          <h4 className="header-text">{titleText}</h4>
        </div>

        <div className="body-wrapper">
          <p className="body-text">{subtitleText}</p>
        </div>
        <AlertComponent
            message={alertMessage}
            type={alertType ?? "info"}
            open={alertOpen}
            onClose={() => setAlertOpen(false)}
        />


        <div className="form-wrapper">
          <Formik
            initialValues={{ password: "", confirmPassword: "" }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              handleChange,
              handleBlur,
              touched,
              errors,
              isValid,
              dirty,
              isSubmitting,
            }) => (
              <Form>
                <div className="form-fields">
                  <div className="form-label">
                    New Password <span className="form-label-required">*</span>
                  </div>

                  <OutlinedInput
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={values.password}
                    placeholder="Enter new password"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.password && Boolean(errors.password)}
                    className="form-field"
                    fullWidth
                    startAdornment={
                      <InputAdornment position="start">
                        <img src={LockIcon} alt="Lock" />
                      </InputAdornment>
                    }
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((s) => !s)}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />

                  {touched.password && errors.password && (
                    <div className="error-message">{errors.password}</div>
                  )}

                  <div className="form-label">
                    Confirm New Password{" "}
                    <span className="form-label-required">*</span>
                  </div>

                  <OutlinedInput
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={values.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm new password"
                    onBlur={handleBlur}
                    error={
                      touched.confirmPassword &&
                      Boolean(errors.confirmPassword)
                    }
                    className="form-field"
                    fullWidth
                    startAdornment={
                      <InputAdornment position="start">
                        <img src={LockIcon} alt="Lock" />
                      </InputAdornment>
                    }
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setShowConfirmPassword((s) => !s)
                          }
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showConfirmPassword ? (
                            <Visibility />
                          ) : (
                            <VisibilityOff />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                  />

                  {touched.confirmPassword && errors.confirmPassword && (
                    <div className="error-message">
                      {errors.confirmPassword}
                    </div>
                  )}

                  {successMsg && (
                    <div className="success-message">{successMsg}</div>
                  )}
                  {/* {apiError && (
                    <div className="error-message">{apiError}</div>
                  )} */}
                </div>

                <DialogActions>
                  <button
                    className="login-btn"
                    type="submit"
                    disabled={isSubmitting || !isValid || !dirty}
                  >
                    {isSubmitting ? "Updating..." : "Reset Password"}
                  </button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ResetPasswordPopup;
